(function () {
  function clamp(n, a, b) {
    return Math.max(a, Math.min(b, n));
  }

  function init(root) {
    var viewport = root.querySelector("[data-ir-results-viewport]");
    var controls = root.querySelector("[data-ir-results-controls]");
    var range = root.querySelector("[data-ir-results-range]");
    var prev = root.querySelector("[data-ir-results-prev]");
    var next = root.querySelector("[data-ir-results-next]");
    var track = root.querySelector(".ir-results__track");
    if (!viewport || !controls || !range || !prev || !next || !track) return;

    var isSyncingFromRange = false;
    var isSyncingFromScroll = false;

    // ── Scroll-active guard ───────────────────────────────────────────────────
    var isScrolling = false;
    var scrollSettleTimer = null;

    // ── Smooth-scroll animation timer ─────────────────────────────────────────
    // Used to restore scroll-snap after a programmatic smooth scroll completes.
    var scrollAnimTimer = null;

    // ── Auto-scroll state ─────────────────────────────────────────────────────
    var autoScrollInterval = null;
    var AUTO_SCROLL_DELAY = 2500; // ms between advances

    function startAutoScroll() {
      stopAutoScroll();
      autoScrollInterval = setInterval(function () {
        if (isScrolling) return; // skip — a scroll is still in progress
        var count = getCardCount();
        var nextIdx = getCurrentIndex() + 1;
        if (nextIdx >= count) {
          // Disable snap BEFORE the jump, restore AFTER — same pattern as
          // scrollToIndex. Restoring snap first then jumping caused iOS to
          // briefly run an animated snap evaluation from maxScroll, which
          // made the first card appear off-center for a moment.
          clearTimeout(scrollAnimTimer);
          viewport.style.scrollSnapType = "none";
          viewport.scrollLeft = 0;
          scrollAnimTimer = setTimeout(function () {
            viewport.style.scrollSnapType = "";
          }, 50);
        } else {
          scrollToIndex(nextIdx);
        }
      }, AUTO_SCROLL_DELAY);
    }

    function stopAutoScroll() {
      if (autoScrollInterval !== null) {
        clearInterval(autoScrollInterval);
        autoScrollInterval = null;
      }
    }

    function resetAutoScroll() {
      stopAutoScroll();
      startAutoScroll();
    }
    // ─────────────────────────────────────────────────────────────────────────

    function getMaxScroll() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    function getCardCount() {
      return track.querySelectorAll(".ir-result-card").length;
    }

    function getStep() {
      var first = track.querySelector(".ir-result-card");
      if (!first) return 0;
      var styles = window.getComputedStyle(track);
      var gap = parseFloat(styles.columnGap || styles.gap || "0") || 0;
      // offsetWidth is layout-based and unaffected by CSS transforms, so it
      // stays accurate while entry animations (scale/translate) are running.
      return first.offsetWidth + gap;
    }

    function getCurrentIndex() {
      var step = getStep();
      if (!step) return 0;
      return Math.round(viewport.scrollLeft / step);
    }

    // Scroll to an exact card index position.
    // On iOS Safari, scroll-snap-type + smooth programmatic scrolling conflict:
    // the snap correction fires on top of the smooth scroll and lands the
    // viewport between snap points. The fix is to disable snap for the
    // duration of the animation and restore it once the scroll settles.
    function scrollToIndex(idx) {
      var step = getStep();
      if (!step) return;
      var cards = track.querySelectorAll(".ir-result-card");
      var count = cards.length;
      idx = Math.max(0, Math.min(count - 1, idx));
      var targetLeft = Math.round(idx * step);

      // Cards start invisible (opacity:0, scale:0.9) and only animate when
      // the IntersectionObserver sees them enter the viewport. On the first
      // auto-scroll cycle this means the card is blank while the scroll is
      // in progress and for ~1 s after. Pre-trigger the animation here so
      // the card is fully visible by the time it arrives on screen.
      var targetCard = cards[idx];
      if (targetCard && !targetCard.classList.contains("ir-animated")) {
        targetCard.classList.add("ir-animated");
      }

      clearTimeout(scrollAnimTimer);
      viewport.style.scrollSnapType = "none";
      viewport.scrollTo({ left: targetLeft, behavior: "smooth" });

      scrollAnimTimer = setTimeout(function () {
        viewport.style.scrollSnapType = "";
        // If the smooth scroll landed slightly off (sub-pixel on retina),
        // snap it exactly — this is instant and invisible to the user.
        if (Math.abs(viewport.scrollLeft - targetLeft) > 1) {
          viewport.scrollLeft = targetLeft;
        }
      }, 550);
    }

    function updateButtons() {
      var maxScroll = getMaxScroll();
      prev.disabled = viewport.scrollLeft <= 1;
      next.disabled = viewport.scrollLeft >= maxScroll - 1;
      prev.style.opacity = prev.disabled ? "0.45" : "1";
      next.style.opacity = next.disabled ? "0.45" : "1";
    }

    function updateThumbWidth() {
      var trackW = range.getBoundingClientRect().width;
      if (trackW <= 0) return;

      var maxScroll = getMaxScroll();
      var visibleRatio = viewport.scrollWidth <= 0 ? 1 : viewport.clientWidth / viewport.scrollWidth;

      var thumbW = clamp(trackW * visibleRatio, 70, trackW);
      range.style.setProperty("--ir-thumb-w", thumbW + "px");

      range.disabled = maxScroll <= 0;
    }

    function syncRangeFromScroll() {
      if (isSyncingFromRange) return;

      var maxScroll = getMaxScroll();
      var ratio = maxScroll <= 0 ? 0 : viewport.scrollLeft / maxScroll;
      var v = Math.round(clamp(ratio, 0, 1) * 1000);

      isSyncingFromScroll = true;
      range.value = String(v);
      isSyncingFromScroll = false;

      updateButtons();
    }

    function syncScrollFromRange() {
      if (isSyncingFromScroll) return;

      var maxScroll = getMaxScroll();
      var v = parseInt(range.value || "0", 10);
      var ratio = clamp(v / 1000, 0, 1);
      var x = ratio * maxScroll;

      isSyncingFromRange = true;
      viewport.scrollTo({ left: x, behavior: "auto" });
      isSyncingFromRange = false;

      updateButtons();
    }

    function scrollByDir(dir) {
      scrollToIndex(getCurrentIndex() + dir);
    }

    // ── User interaction → pause auto-scroll, then resume ────────────────────
    prev.addEventListener("click", function () {
      scrollByDir(-1);
      resetAutoScroll();
    });

    next.addEventListener("click", function () {
      scrollByDir(1);
      resetAutoScroll();
    });

    range.addEventListener("input", function () {
      syncScrollFromRange();
      resetAutoScroll();
    });

    // Pause while the user is hovering over the slider section
    root.addEventListener("mouseenter", stopAutoScroll);
    root.addEventListener("mouseleave", startAutoScroll);

    // Pause while the user is touching (mobile)
    root.addEventListener("touchstart", stopAutoScroll, { passive: true });
    root.addEventListener("touchend", startAutoScroll, { passive: true });

    viewport.addEventListener(
      "scroll",
      function () {
        isScrolling = true;
        clearTimeout(scrollSettleTimer);
        scrollSettleTimer = setTimeout(function () {
          isScrolling = false;
        }, 250);
        syncRangeFromScroll();
      },
      { passive: true }
    );
    // ─────────────────────────────────────────────────────────────────────────

    function refresh() {
      updateThumbWidth();
      syncRangeFromScroll();
      updateButtons();
    }

    window.addEventListener("resize", function () {
      refresh();
    });

    if (window.ResizeObserver) {
      new ResizeObserver(function () {
        refresh();
      }).observe(viewport);
    }

    // Defer first refresh so layout is fully computed (avoids intermittent
    // misalignment caused by animation transforms affecting getBoundingClientRect
    // during the initial synchronous render pass).
    requestAnimationFrame(function () {
      refresh();
      // Second pass after CSS transitions finish (longest delay is ~900ms)
      setTimeout(refresh, 1000);
    });

    // Kick off auto-scroll
    startAutoScroll();
  }

  function boot() {
    var nodes = document.querySelectorAll("[data-ir-results]");
    if (!nodes.length) return;
    nodes.forEach(init);
  }

  // IR Real Results Animation System
  class IRResultsAnimations {
    constructor() {
      this.animatedElements = [];
      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupAnimations());
      } else {
        this.setupAnimations();
      }
    }

    setupAnimations() {
      const animateElements = document.querySelectorAll('.ir-animate-fade-up, .ir-animate-scale, .ir-animate-slide-up');
      if (animateElements.length === 0) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.animateElement(entry.target);
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      });

      animateElements.forEach(element => {
        observer.observe(element);
      });
    }

    animateElement(element) {
      const delay = parseInt(element.getAttribute('data-delay')) || 0;
      setTimeout(() => {
        element.classList.add('ir-animated');
      }, delay);
    }
  }

  const irResultsAnimations = new IRResultsAnimations();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();